import React from "react";
import "./css/index.css";
import "./css/PixelImage.css";
import "./css/KnittingOrder.css";
import ExtentionTabs from "./ExtensionTabs.js";
import { arrayMoveImmutable as arrayMove } from "array-move";
import {
  ArrowBack,
  ArrowBackIos,
  ArrowForward,
  ArrowForwardIos,
} from "@material-ui/icons";

const BASE_CELL_SIZE = 15;
const CELL_BORDER = 1;
const CELL_TOTAL = BASE_CELL_SIZE + CELL_BORDER;

// Pick black or white text depending on how light the background hex color is
const getContrastTextColor = (color) => {
  let hex = String(color || "").replace("#", "");
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return "#000000";

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  return brightness < 128 ? "#ffffff" : "#000000";
};

const PixelCounter = ({ color, count }) => {
  return (
    <li
      className="pixel-counter"
      style={{ backgroundColor: color, color: getContrastTextColor(color) }}
    >
      <div>{count}</div>
    </li>
  );
};

class PixelCounterNav extends React.Component {
  render() {
    const { colors = [], row = [] } = this.props;

    return (
      <nav>
        <ul className="colors">
          {row.map((pixel, i) => (
            <PixelCounter
              key={`pixel-counter-${i}`}
              count={pixel.count}
              color={colors[pixel.color] || "#000000"}
            />
          ))}
        </ul>
      </nav>
    );
  }
}

class Pagination extends React.Component {
  previousClick = () => {
    const { row, onClick } = this.props;
    if (onClick) onClick(row - 1);
  };

  nextClick = () => {
    const { row, onClick } = this.props;
    if (onClick) onClick(row + 1);
  };

  render() {
    const { row, motifHeight } = this.props;

    return (
      <div className="pagination">
        <div>
          <div
            className="prev"
            aria-label="Previous"
            onClick={this.previousClick}
          >
            <ArrowBackIos style={{ fontSize: 18 }} />
          </div>
        </div>

        <div>
          <div className="display">Current row</div>
          <input
            type="number"
            className="displayInput"
            value={motifHeight - row}
            readOnly
          />
        </div>

        <div>
          <div className="next" aria-label="Next" onClick={this.nextClick}>
            <ArrowForwardIos style={{ fontSize: 18 }} />
          </div>
        </div>
      </div>
    );
  }
}

const Pixel = ({ color, count }) => {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div
          className="pixel-box"
          style={{ backgroundColor: color }}
          key={i}
        />
      ))}
    </>
  );
};

class MotifRow extends React.Component {
  handleClick = () => {
    const { onClick, index } = this.props;
    if (onClick) onClick(index);
  };

  render() {
    const {
      colors = [],
      pixels = [],
      rowNr,
      isActive,
      index,
      rightToLeft,
      knittingInTheRound,
      descending,
      totalrows,
    } = this.props;

    const knittingRowIndex = descending ? totalrows - 1 - index : index;

    const showForward = knittingInTheRound
      ? !rightToLeft
      : knittingRowIndex % 2 === 0
        ? !rightToLeft
        : rightToLeft;

    return (
      <div
        className={isActive ? "row selected_row" : "row"}
        onClick={this.handleClick}
      >
        <div className="pixel-box number-cell">
          {isActive ? (
            showForward ? (
              <ArrowForward fontSize="small" />
            ) : (
              <ArrowBack fontSize="small" />
            )
          ) : (
            rowNr
          )}
        </div>

        {pixels.map((pixel, i) => (
          <Pixel
            key={`pixel-${index}-${i}`}
            color={colors[pixel.color] || "#000000"}
            count={pixel.count}
          />
        ))}

        <div className="pixel-box number-cell">
          {isActive ? (
            showForward ? (
              <ArrowForward fontSize="small" />
            ) : (
              <ArrowBack fontSize="small" />
            )
          ) : (
            rowNr
          )}
        </div>
      </div>
    );
  }
}

class Motif extends React.Component {
  constructor(props) {
    super(props);

    const input = props.input || {};
    const rows = Array.isArray(input.rows) ? input.rows : [];
    const colors = Array.isArray(input.colors) ? input.colors : [];

    this.state = {
      current: {
        ...input,
        rows,
        colors,
        width: input.width || 0,
        height: input.height || rows.length || 0,
      },
      colors,
      selectedIndex: 0,
      motifScale: 1,
      descending: false,
      rightToLeft: false,
      flatKnitting: true,
      knittingInTheRound: false,
    };

    this.motifViewportRef = React.createRef();
    this.motifWrapperRef = React.createRef();

    this.handleClick = this.handleClick.bind(this);
    this.handleColorChange = this.handleColorChange.bind(this);
    this.onSortEnd = this.onSortEnd.bind(this);
    this.updateScale = this.updateScale.bind(this);
    this.handleOrderChange = this.handleOrderChange.bind(this);
    this.handleDirectionChange = this.handleDirectionChange.bind(this);
    this.handleKnittingInTheRoundChange =
      this.handleKnittingInTheRoundChange.bind(this);
  }

  componentDidMount() {
    this.updateScale();
    window.addEventListener("resize", this.updateScale);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.input !== this.props.input) {
      const input = this.props.input || {};
      const rows = Array.isArray(input.rows) ? input.rows : [];
      const colors = Array.isArray(input.colors) ? input.colors : [];

      this.setState(
        {
          current: {
            ...input,
            rows,
            colors,
            width: input.width || 0,
            height: input.height || rows.length || 0,
          },
          colors,
          selectedIndex: 0,
        },
        this.updateScale
      );
      return;
    }

    const widthChanged =
      prevState.current.width !== this.state.current.width ||
      prevState.current.height !== this.state.current.height;

    if (widthChanged) {
      this.updateScale();
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateScale);
  }

  handleClick(index) {
    const rowCount = this.state.current?.rows?.length || 0;

    if (index >= 0 && index < rowCount) {
      this.setState({ selectedIndex: index });
    }
  }

  handleColorChange(color, ind) {
    const colors = [...this.state.colors];
    colors[ind] = color;
    this.setState({ colors });
  }

  handleOrderChange(descending) {
    this.setState({ descending });
  }

  handleDirectionChange(rightToLeft) {
    this.setState({ rightToLeft });
  }

  handleKnittingInTheRoundChange(knittingInTheRound) {
    this.setState((prevState) => ({
      knittingInTheRound,
      flatKnitting: !knittingInTheRound,
      rightToLeft: knittingInTheRound ? prevState.rightToLeft : false,
    }));
  }

  onSortEnd(oldIndex, newIndex) {
    const colors = this.state.colors || [];
    this.setState({ colors: arrayMove(colors, oldIndex, newIndex) });
  }

  updateScale() {
    const input = this.state.current;
    const viewport = this.motifViewportRef.current;

    if (!viewport || !input) return;

    const motifWidth = (input.width + 2) * CELL_TOTAL;
    const availableWidth = viewport.clientWidth - 8;
    const scale = Math.min(1, availableWidth / motifWidth);

    this.setState({ motifScale: scale > 0 ? scale : 1 });
  }

  renderBottomNumberRow(input) {
    const { descending } = this.state;

    return (
      <div className="row-end">
        <div className="pixel-box number-cell"></div>

        {Array.from({ length: input.width }).map((_, ind) => (
          <div className="pixel-box number-cell" key={`bottom-${ind}`}>
            {descending ? input.width - ind : ind + 1}
          </div>
        ))}

        <div className="pixel-box number-cell"></div>
      </div>
    );
  }

  render() {
    const input = this.state.current || {};
    const rows = Array.isArray(input.rows) ? input.rows : [];
    const colors = Array.isArray(this.state.colors) ? this.state.colors : [];
    const {
      descending,
      selectedIndex,
      motifScale,
      rightToLeft,
      flatKnitting,
      knittingInTheRound,
    } = this.state;

    if (!rows.length) {
      return <div className="motif-wrapper">No motif data available.</div>;
    }

    const safeSelectedIndex = Math.min(selectedIndex, rows.length - 1);
    const selectedRow = rows[safeSelectedIndex];
    const selectedPixels = selectedRow?.pixels || [];

    const motifWidth = (input.width + 2) * CELL_TOTAL;
    const motifHeight = (input.height + 1) * CELL_TOTAL;
    const scaledWidth = motifWidth * motifScale;
    const scaledHeight = motifHeight * motifScale;

    return (
      <div className="motif-wrapper" ref={this.motifWrapperRef}>
        <ExtentionTabs
          colors={colors}
          rows={rows}
          row={selectedPixels}
          onHandleColorChange={this.handleColorChange}
          onSortEnd={this.onSortEnd}
          pickerAnchorRef={this.motifWrapperRef}
          descending={descending}
          rightToLeft={rightToLeft}
          flatKnitting={flatKnitting}
          knittingInTheRound={knittingInTheRound}
          onOrderChange={this.handleOrderChange}
          onDirectionChange={this.handleDirectionChange}
          onKnittingInTheRoundChange={this.handleKnittingInTheRoundChange}
        />

        <div className="motif-viewport" ref={this.motifViewportRef}>
          <div
            className="motif-scale-space"
            style={{ width: `${scaledWidth}px`, height: `${scaledHeight}px` }}
          >
            <div
              className="motif"
              style={{
                width: `${motifWidth}px`,
                transform: `scale(${motifScale})`,
                transformOrigin: "top left",
              }}
            >
              {rows.map((item, k) => (
                <MotifRow
                  index={k}
                  key={k}
                  isActive={safeSelectedIndex === k}
                  onClick={this.handleClick}
                  pixels={item.pixels || []}
                  colors={colors}
                  rowNr={descending ? input.height - k : k + 1}
                  rightToLeft={rightToLeft}
                  knittingInTheRound={knittingInTheRound}
                  descending={descending}
                  totalrows={rows.length}
                />
              ))}

              {this.renderBottomNumberRow(input)}
            </div>
          </div>
        </div>

        <PixelCounterNav row={selectedPixels} colors={colors} />

        <Pagination
          row={safeSelectedIndex}
          motifHeight={input.height}
          onClick={this.handleClick}
        />
      </div>
    );
  }
}

export default Motif;